import React, { useCallback, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import type {
  RecordingScheduleDayKey,
  RecordingScheduleSlots,
} from "../../types/recordingSchedule.ts";
import {
  RECORDING_SCHEDULE_DAY_KEYS,
  RECORDING_SCHEDULE_DAY_LABELS,
  SLOTS_PER_DAY,
  cloneRecordingScheduleSlots,
  formatSlotRowLabel,
} from "../../lib/recording-schedule.ts";

const ACCENT = "#2932E5";
const BORDER = "#E5E7EB";
const SLOT_INDICES = Array.from({ length: SLOTS_PER_DAY }, (_, i) => i);

export interface RecordingScheduleGridProps {
  value: RecordingScheduleSlots;
  onChange: (next: RecordingScheduleSlots) => void;
}

export function RecordingScheduleGrid({
  value,
  onChange,
}: RecordingScheduleGridProps): React.ReactElement {
  const paintRef = useRef<"select" | "erase" | null>(null);

  useEffect(() => {
    const endPaint = (): void => {
      paintRef.current = null;
    };
    window.addEventListener("pointerup", endPaint);
    window.addEventListener("pointercancel", endPaint);
    return () => {
      window.removeEventListener("pointerup", endPaint);
      window.removeEventListener("pointercancel", endPaint);
    };
  }, []);

  const setSlot = useCallback(
    (day: RecordingScheduleDayKey, slotIndex: number, selected: boolean) => {
      if (value[day][slotIndex] === selected) return;
      const next = cloneRecordingScheduleSlots(value);
      next[day][slotIndex] = selected;
      onChange(next);
    },
    [onChange, value],
  );

  const setAll = useCallback(
    (selected: boolean) => {
      const next = cloneRecordingScheduleSlots(value);
      for (const day of RECORDING_SCHEDULE_DAY_KEYS) {
        for (let i = 0; i < SLOTS_PER_DAY; i += 1) {
          next[day][i] = selected;
        }
      }
      onChange(next);
    },
    [onChange, value],
  );

  const setDayAll = useCallback(
    (day: RecordingScheduleDayKey, selected: boolean) => {
      const next = cloneRecordingScheduleSlots(value);
      for (let i = 0; i < SLOTS_PER_DAY; i += 1) {
        next[day][i] = selected;
      }
      onChange(next);
    },
    [onChange, value],
  );

  const toggleAllDay = useCallback(
    (day: RecordingScheduleDayKey) => {
      const anyOff = value[day].some((v) => !v);
      setDayAll(day, anyOff);
    },
    [setDayAll, value],
  );

  const onCellPointerDown = (
    day: RecordingScheduleDayKey,
    slotIndex: number,
    e: React.PointerEvent,
  ) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const nextSelected = !value[day][slotIndex];
    paintRef.current = nextSelected ? "select" : "erase";
    setSlot(day, slotIndex, nextSelected);
  };

  const onCellPointerEnter = (
    day: RecordingScheduleDayKey,
    slotIndex: number,
    e: React.PointerEvent,
  ) => {
    if (paintRef.current === null) return;
    if ((e.buttons & 1) === 0) return;
    setSlot(day, slotIndex, paintRef.current === "select");
  };

  const toolBtnSx = {
    fontFamily: "Roboto, sans-serif",
    fontSize: 12,
    textTransform: "none" as const,
    borderColor: BORDER,
    color: "#374151",
    "&:hover": { borderColor: ACCENT, color: ACCENT, bgcolor: "#F9FAFB" },
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Button size="small" variant="outlined" sx={toolBtnSx} onClick={() => setAll(true)}>
          Select all
        </Button>
        <Button size="small" variant="outlined" sx={toolBtnSx} onClick={() => setAll(false)}>
          Clear all
        </Button>
      </Box>

      <Box
        sx={{
          overflow: "auto",
          maxHeight: "min(70vh, 520px)",
          border: `1px solid ${BORDER}`,
          borderRadius: 1,
          bgcolor: "#fff",
        }}
      >
        <Table
          size="small"
          stickyHeader
          aria-label="Weekly recording schedule in 30-minute slots"
          sx={{
            minWidth: 640,
            "& .MuiTableCell-root": {
              border: `1px solid ${BORDER}`,
              p: 0,
              fontSize: 11,
              fontFamily: "Roboto, sans-serif",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  position: "sticky",
                  left: 0,
                  zIndex: 3,
                  bgcolor: "#F9FAFB",
                  color: "#6B7280",
                  fontWeight: 500,
                  width: 56,
                  px: 1,
                  py: 1,
                }}
              >
                Time
              </TableCell>
              {RECORDING_SCHEDULE_DAY_KEYS.map((day) => (
                <TableCell
                  key={day}
                  align="center"
                  sx={{ bgcolor: "#F3F4F6", fontWeight: 600, color: "#111827", py: 0.5 }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                    {RECORDING_SCHEDULE_DAY_LABELS[day]}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => toggleAllDay(day)}
                    sx={{
                      mt: 0.25,
                      minWidth: 0,
                      fontSize: 10,
                      textTransform: "none",
                      color: "#6B7280",
                      "&:hover": { color: ACCENT, bgcolor: "transparent" },
                    }}
                  >
                    All day
                  </Button>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {SLOT_INDICES.map((slotIndex) => (
              <TableRow key={slotIndex} sx={{ height: 22 }}>
                <TableCell
                  component="th"
                  scope="row"
                  sx={{
                    position: "sticky",
                    left: 0,
                    zIndex: 2,
                    bgcolor: "#fff",
                    color: slotIndex % 2 === 0 ? "#111827" : "#9CA3AF",
                    fontWeight: 400,
                    whiteSpace: "nowrap",
                    px: 1,
                  }}
                >
                  {formatSlotRowLabel(slotIndex)}
                </TableCell>
                {RECORDING_SCHEDULE_DAY_KEYS.map((day) => {
                  const selected = value[day][slotIndex];
                  return (
                    <TableCell key={`${day}-${slotIndex}`} sx={{ p: 0 }}>
                      <Box
                        component="button"
                        type="button"
                        aria-selected={selected}
                        onPointerDown={(e) => onCellPointerDown(day, slotIndex, e)}
                        onPointerEnter={(e) => onCellPointerEnter(day, slotIndex, e)}
                        sx={{
                          display: "block",
                          width: "100%",
                          minWidth: 28,
                          height: 22,
                          border: 0,
                          cursor: "pointer",
                          bgcolor: selected ? "rgba(41, 50, 229, 0.28)" : "#F9FAFB",
                          "&:hover": {
                            bgcolor: selected
                              ? "rgba(41, 50, 229, 0.38)"
                              : "#F3F4F6",
                          },
                        }}
                      />
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Typography
        sx={{
          fontFamily: "Roboto, sans-serif",
          fontSize: 11,
          color: "#6B7280",
          lineHeight: 1.5,
        }}
      >
        Selected slots (blue tint) are recorded when the camera is in scheduled mode,
        active, and recording is enabled. Times use the effective timezone saved with
        this schedule (browser zone on save, or server default).
      </Typography>
    </Box>
  );
}
